import { useEffect, useState } from '/vendor/htm-preact.module.js';

/**
 * A QR code, fetched only when something actually shows one.
 *
 * The library is 56 KB unpacked — the largest single thing the app loads —
 * and it was in the module graph every screen waits for, so everybody paid to
 * parse it whether or not they ever tapped "Let people watch" or looked at an
 * invite. Most sessions never draw a QR at all.
 *
 * It stays in the service worker's shell list, so the one place it really
 * matters — a phone in a cellar being shown an invite — still works offline.
 * Precaching happens in the background after install; importing does not.
 */
let loading = null;

function library() {
  if (!loading) {
    loading = import('/vendor/qrcode.module.js')
      .then((m) => m.default)
      .catch((e) => { loading = null; throw e; });
  }
  return loading;
}

/**
 * The SVG for `text`, or null while it arrives, or '' if it cannot be drawn.
 *
 * Three callers built this by hand identically; the only thing they disagreed
 * about was nothing at all.
 */
export function useQrSvg(text, { cellSize = 5, margin = 2 } = {}) {
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    let live = true;
    if (!text) { setSvg(''); return undefined; }
    library().then((qrcode) => {
      const qr = qrcode(0, 'M');
      qr.addData(text);
      qr.make();
      if (live) setSvg(qr.createSvgTag({ cellSize, margin }));
    }).catch(() => { if (live) setSvg(''); });
    return () => { live = false; };
  }, [text, cellSize, margin]);

  return svg;
}
